import { useManager } from 'dreamstate';
import { useEffect } from 'react';

import { Maybe, Optional } from '@vimeo/core/types/generic';

import { useLiveGlobals } from '../components/providers/globals';
import { ILiveTrackingConfig, liveTrackingConfig } from '../configs/LiveTrackingConfig';
import { ComposerSessionManager } from '../store/composer/session';
import { ComposerSessionStatusManager } from '../store/composer/status';
import { EventSettingsManager } from '../store/event/settings';
import { GuestSessionManager } from '../store/guest/session';
import { InteractionSessionManager } from '../store/interaction/session';
import { TrackingManager } from '../store/tracking';
import { IVimeoApiConnection } from '../types/api';
import { EComposerSessionType, EComposerStreamModeType } from '../types/composer_session';
import { EIngestStatus } from '../types/event';
import { IInteractionStoreInitialState } from '../types/store';
import { parseClipIdFromURI } from '../utils/general/parse';
import { ProductionMethod } from './helpers/types';
import { isInteractionTrackingConfigReady, isLiveTrackingConfigReady } from './helpers/utils';

/**
 * Subscribe to guest session and update tracking config.
 */
export function useGuestTrackingConfigUpdater(): void {
  const {
    sessionType,
    sessionId,
    sessionInfo: { value: sessionInfo },
  } = useManager(GuestSessionManager);
  const { ingestStatus, cloudSessionId, streamMode } = useManager(ComposerSessionStatusManager);

  const { trackingActions } = useManager(TrackingManager, ({ trackingActions }) => [
    trackingActions,
  ]);

  useEffect(() => {
    const connection: Maybe<IVimeoApiConnection> = sessionInfo?.metadata.connections.ingestStatus;
    const possibleVideoId: Optional<number> =
      (connection && parseClipIdFromURI(connection.uri)) || null;
    const liveStatus: string = EIngestStatus[ingestStatus].toLowerCase();

    trackingActions.setConfig({
      DETAILS: {
        SESSION_CLOUD_ID: cloudSessionId,
      },
      BIG_PICTURE_LIVE_SCHEMA_BASE: {
        page: liveTrackingConfig.DETAILS.SESSION_APPLICATION,
        liveEventType: sessionType,
        liveEventId: sessionId ? String(sessionId) : null,
        liveStatus,
        videoId: possibleVideoId,
      },
      BIG_PICTURE_INTERACTION_SCHEMA_BASE: {
        page: liveTrackingConfig.DETAILS.SESSION_APPLICATION,
        view: 'attendee',
        liveStatus,
        videoId: possibleVideoId,
        roomType: sessionType,
        roomId: sessionId ? String(sessionId) : null,
      },
    });

    if (isLiveTrackingConfigReady(liveTrackingConfig)) {
      trackingActions.setIsReady();
    }
  }, [
    trackingActions,
    sessionId,
    sessionType,
    ingestStatus,
    cloudSessionId,
    sessionInfo,
    streamMode,
  ]);
}

/**
 * Subscribe to interaction session and update tracking config.
 */
export function useInteractionToolsConfigUpdater(): {
  trackingConfig: ILiveTrackingConfig;
  isTrackingReady: boolean;
} {
  const {
    sessionType,
    sessionId,
    roomInfo: { value: roomInfo },
    roomUser: { value: roomUser },
  } = useManager(InteractionSessionManager);

  const {
    trackingActions,
    config: trackingConfig,
    isReady: isTrackingReady,
  } = useManager(TrackingManager, ({ config, isReady }) => [config, isReady]);

  const {
    initialState: { interaction },
  } = useLiveGlobals<IInteractionStoreInitialState>();

  useEffect(() => {
    const connection: Maybe<IVimeoApiConnection> = roomInfo?.metadata.connections.ingestStatus;
    const possibleClipId: Maybe<number> = connection ? parseClipIdFromURI(connection.uri) : null;
    const isAttendee: boolean = Boolean(
      interaction?.feature?.isManagementDisabled || !roomUser?.isCreator,
    );

    const config = {
      BIG_PICTURE_INTERACTION_SCHEMA_BASE: {
        page: liveTrackingConfig.DETAILS.SESSION_APPLICATION,
        view: isAttendee ? 'attendee' : 'moderator',
        videoId: possibleClipId,
        roomType: sessionType,
        roomId: sessionId ? String(sessionId) : null,
      },
    };

    if (sessionType === EComposerSessionType.VENUE) {
      config['BIG_PICTURE_VENUE_SCHEMA_BASE'] = {
        ...roomInfo?.metadata?.misc?.tracking,
      };
    }

    trackingActions.setConfig(config);

    if (!isTrackingReady && isInteractionTrackingConfigReady(liveTrackingConfig)) {
      trackingActions.setIsReady();
    }
  }, [
    trackingActions,
    sessionId,
    sessionType,
    roomInfo,
    isTrackingReady,
    interaction?.feature?.isManagementDisabled,
    roomUser?.isCreator,
  ]);

  return { isTrackingReady, trackingConfig };
}

/**
 * Subscribe to event context and update tracking config.
 */
export function useBroadcasterTrackingConfigUpdater(): {
  trackingConfig: ILiveTrackingConfig;
  isTrackingReady: boolean;
} {
  const {
    sessionId,
    sessionType,
    sessionInfo: { value: sessionInfo },
  } = useManager(ComposerSessionManager);
  const { ingestStatus, cloudSessionId, streamMode } = useManager(ComposerSessionStatusManager);
  const {
    trackingActions,
    config: trackingConfig,
    isReady: isTrackingReady,
  } = useManager(TrackingManager, ({ config, isReady }) => [config, isReady]);
  const {
    settings: { value: eventSettings },
  } = useManager(EventSettingsManager);

  /**
   * Keep live event info in sync.
   */
  useEffect(() => {
    const connection: Maybe<IVimeoApiConnection> = sessionInfo?.metadata.connections.ingestStatus;
    const possibleVideoId: Optional<number> =
      (connection && parseClipIdFromURI(connection.uri)) || null;
    const liveStatus: string = EIngestStatus[ingestStatus].toLowerCase();
    const productionMethod = eventSettings?.preferredStreamMethod
      ? ProductionMethod[eventSettings.preferredStreamMethod]
      : null;

    trackingActions.setConfig({
      DETAILS: {
        SESSION_CLOUD_ID: cloudSessionId,
      },
      BIG_PICTURE_LIVE_SCHEMA_BASE: {
        page: liveTrackingConfig.DETAILS.SESSION_APPLICATION,
        liveEventType: sessionType,
        liveEventId: sessionId ? String(sessionId) : null,
        liveStatus,
        videoId: possibleVideoId,
      },
      BIG_PICTURE_INTERACTION_SCHEMA_BASE: {
        page: liveTrackingConfig.DETAILS.SESSION_APPLICATION,
        view: 'moderator',
        liveStatus,
        videoId: possibleVideoId,
        roomType: sessionType,
        roomId: sessionId ? String(sessionId) : null,
      },
      BIG_PICTURE_SCHEME_ADDITIONAL: {
        isSimulive: streamMode === EComposerStreamModeType.SIMULIVE,
        isRecord: streamMode === EComposerStreamModeType.RECORD,
      },
      BIG_PICTURE_EVENT_SETTINGS_SCHEMA_BASE: {
        eventPrivacy: eventSettings?.streamPrivacy?.view,
        productionMethod,
      },
    });

    if (isLiveTrackingConfigReady(liveTrackingConfig)) {
      trackingActions.setIsReady();
    }
  }, [
    eventSettings?.streamPrivacy?.view,
    eventSettings?.preferredStreamMethod,
    trackingActions,
    sessionType,
    sessionId,
    ingestStatus,
    cloudSessionId,
    sessionInfo?.metadata,
    streamMode,
  ]);

  return { isTrackingReady, trackingConfig };
}

/**
 * Subscribe to event context and update tracking config.
 */
export function useRTMPTrackingConfigUpdater(): {
  trackingConfig: ILiveTrackingConfig;
  isTrackingReady: boolean;
} {
  const {
    sessionId,
    sessionType,
    sessionInfo: { value: sessionInfo },
  } = useManager(ComposerSessionManager);

  const { ingestStatus } = useManager(ComposerSessionStatusManager);

  const {
    trackingActions,
    config: trackingConfig,
    isReady: isTrackingReady,
  } = useManager(TrackingManager, ({ config, isReady }) => [config, isReady]);

  const {
    settings: { value: eventSettings },
  } = useManager(EventSettingsManager);

  /**
   * Keep live event info in sync.
   */
  useEffect(() => {
    const connection: Maybe<IVimeoApiConnection> = sessionInfo?.metadata.connections.ingestStatus;
    const possibleVideoId: Optional<number> =
      (connection && parseClipIdFromURI(connection.uri)) || null;
    const liveStatus: string = EIngestStatus[ingestStatus].toLowerCase();
    const productionMethod = eventSettings?.preferredStreamMethod
      ? ProductionMethod[eventSettings.preferredStreamMethod]
      : null;

    trackingActions.setConfig({
      BIG_PICTURE_LIVE_SCHEMA_BASE: {
        page: liveTrackingConfig.DETAILS.SESSION_APPLICATION,
        liveEventType: sessionType,
        liveEventId: sessionId ? String(sessionId) : null,
        liveStatus,
        videoId: possibleVideoId,
      },
      BIG_PICTURE_EVENT_SETTINGS_SCHEMA_BASE: {
        eventPrivacy: eventSettings?.streamPrivacy?.view,
        productionMethod,
      },
    });

    if (isLiveTrackingConfigReady(liveTrackingConfig)) {
      trackingActions.setIsReady();
    }
  }, [
    eventSettings?.streamPrivacy?.view,
    eventSettings?.preferredStreamMethod,
    trackingActions,
    sessionType,
    sessionId,
    ingestStatus,
    sessionInfo?.metadata,
  ]);

  return { isTrackingReady, trackingConfig };
}
